/**
 * Permission React Hooks
 *
 * Provides React hooks for permission checking with optimized re-rendering
 * and state management integration.
 */

import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { FeaturePermission } from '../constants/featurePermissions';
import {
  PermissionChecker,
  PermissionValidator,
  PermissionContext,
  PermissionRequirement,
  PermissionCheckResult,
  BulkPermissionResult,
  createPermissionChecker
} from '../utils/permissionChecker';

/**
 * Main permission hook - provides a configured permission checker
 */
export const usePermissions = () => {
  const { user, currentCompanyUser, isLoading, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const permissionContext: PermissionContext = useMemo(() => ({
    userPermissions: (currentCompanyUser?.featurePermissions as FeaturePermission[]) || [],
    userRole: user?.role || '',
    companyUser: currentCompanyUser,
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.role === 'super_admin',
    isAuthenticated
  }), [user, currentCompanyUser, isAuthenticated]);

  const checker = useMemo(() =>
    createPermissionChecker(permissionContext),
    [permissionContext]
  );

  const validator = useMemo(() =>
    new PermissionValidator(permissionContext),
    [permissionContext]
  );

  return { checker, validator, context: permissionContext, loading: isLoading };
};

/**
 * Hook for checking a single permission
 */
export const usePermissionCheck = (requirement: PermissionRequirement): PermissionCheckResult => {
  const { checker } = usePermissions();

  return useMemo(() => checker.check(requirement), [checker, requirement]);
};

/**
 * Hook for checking if user has specific feature permission
 */
export const useHasPermission = (feature: FeaturePermission): boolean => {
  const result = usePermissionCheck({ features: [feature] });
  return result.hasAccess;
};

/**
 * Result type for company and user permission check
 */
export interface CompanyAndUserPermissionResult {
  user: boolean;
  company: boolean;
  hasAccess: boolean; // Combined: true only if both user and company have access
}

/**
 * Hook for checking if both company and user have specific feature permission(s)
 * Returns separate booleans for company and user access
 * 
 * @param features - Single feature or array of features to check
 * @param requireAll - If true, all features must be present; if false, any feature is sufficient
 */
export const useCompanyAndUserPermission = (
  features: FeaturePermission | FeaturePermission[],
  requireAll: boolean = false
): CompanyAndUserPermissionResult => {
  const { user, currentCompanyUser } = useSelector((state: RootState) => state.auth);
  const { currentCompany } = useSelector((state: RootState) => state.companies);

  return useMemo(() => {
    // Super admin has full access
    if (user?.role === 'super_admin') {
      return { user: true, company: true, hasAccess: true };
    }

    const featureArray = Array.isArray(features) ? features : [features];
    const userPermissions = (currentCompanyUser?.featurePermissions as FeaturePermission[]) || [];
    const companyFeatures = (currentCompany?.featuresList as string[]) || [];

    // Check user permissions
    let hasUserAccess = false;
    if (userPermissions.length > 0) {
      if (requireAll) {
        hasUserAccess = featureArray.every(feature => userPermissions.includes(feature));
      } else {
        hasUserAccess = featureArray.some(feature => userPermissions.includes(feature));
      }
    }

    // Check company features
    let hasCompanyAccess = false;
    if (companyFeatures.length > 0) {
      if (requireAll) {
        hasCompanyAccess = featureArray.every(feature => companyFeatures.includes(feature));
      } else {
        hasCompanyAccess = featureArray.some(feature => companyFeatures.includes(feature));
      }
    } else {
      // If no company features list exists, assume company has access (legacy behavior)
      hasCompanyAccess = true;
    }

    return {
      user: hasUserAccess,
      company: hasCompanyAccess,
      hasAccess: hasUserAccess && hasCompanyAccess
    };
  }, [user, currentCompanyUser, currentCompany, features, requireAll]);
};

/**
 * Hook for checking multiple permissions
 */
export const useHasAnyPermission = (features: FeaturePermission[]): boolean => {
  const result = usePermissionCheck({ features, requireAll: false });
  return result.hasAccess;
};

/**
 * Hook for checking all permissions required
 */
export const useHasAllPermissions = (features: FeaturePermission[]): boolean => {
  const result = usePermissionCheck({ features, requireAll: true });
  return result.hasAccess;
};

/**
 * Hook for getting user's access level to a resource
 */
export const useAccessLevel = (resource: string): 'none' | 'read' | 'write' | 'admin' => {
  const { checker } = usePermissions();

  return useMemo(() => checker.getAccessLevel(resource), [checker, resource]);
};

/**
 * Hook for checking administrative permissions
 */
export const useAdminAccess = (adminType: 'system' | 'company' | 'users' = 'company'): PermissionCheckResult => {
  const { checker } = usePermissions();

  return useMemo(() => checker.hasAdminAccess(adminType), [checker, adminType]);
};

/**
 * Hook for checking if user can perform actions on a resource
 */
export const useCanPerform = (resource: string) => {
  const { checker } = usePermissions();

  return useMemo(() => ({
    canRead: checker.canPerform('read', resource, 'read').hasAccess,
    canWrite: checker.canPerform('write', resource, 'write').hasAccess,
    canCreate: checker.canPerform('create', resource, 'write').hasAccess,
    canUpdate: checker.canPerform('update', resource, 'write').hasAccess,
    canDelete: checker.canPerform('delete', resource, 'write').hasAccess,
    getLevel: () => checker.getAccessLevel(resource)
  }), [checker, resource]);
};

/**
 * Hook for bulk permission checking
 */
export const useBulkPermissions = (requirements: Record<string, PermissionRequirement>): BulkPermissionResult => {
  const { checker } = usePermissions();

  return useMemo(() => checker.checkMultiple(requirements), [checker, requirements]);
};

/**
 * Hook for form validation permissions
 */
export const useFormPermissions = (formType: string) => {
  const { validator } = usePermissions();

  return useMemo(() => ({
    canCreate: validator.validateFormSubmission(formType, 'create').valid,
    canUpdate: validator.validateFormSubmission(formType, 'update').valid,
    canDelete: validator.validateFormSubmission(formType, 'delete').valid,
    validateCreate: () => validator.validateFormSubmission(formType, 'create'),
    validateUpdate: () => validator.validateFormSubmission(formType, 'update'),
    validateDelete: () => validator.validateFormSubmission(formType, 'delete')
  }), [validator, formType]);
};

/**
 * Hook for navigation permission checking
 */
export const useNavigationPermissions = () => {
  const { checker } = usePermissions();

  const canAccessRoute = useCallback((
    routePermissions: FeaturePermission[],
    requireAll = false
  ): boolean => {
    return checker.canAccessRoute(routePermissions, requireAll).hasAccess;
  }, [checker]);

  return { canAccessRoute };
};

/**
 * Hook for checking elevated permissions
 */
export const useElevatedPermissions = () => {
  const { checker } = usePermissions();

  return useMemo(() => ({
    hasElevatedAccess: checker.hasElevatedAccess(),
    isAdmin: checker.hasAdminAccess('company').hasAccess,
    isSystemAdmin: checker.hasAdminAccess('system').hasAccess,
    canManageUsers: checker.hasAdminAccess('users').hasAccess
  }), [checker]);
};

/**
 * Hook for permission debugging (development only)
 */
export const usePermissionDebug = () => {
  const { context } = usePermissions();

  return useMemo(() => {
    if (process.env.NODE_ENV !== 'development') {
      return null;
    }

    return {
      context,
      summary: {
        userRole: context.userRole,
        totalPermissions: context.userPermissions.length,
        permissions: context.userPermissions,
        hasElevatedAccess: context.isAdmin || context.isSuperAdmin
      }
    };
  }, [context]);
};

/**
 * Hook for conditional rendering based on permissions
 */
export const useConditionalRender = () => {
  const { checker } = usePermissions();

  const renderIf = useCallback((
    requirement: PermissionRequirement,
    component: React.ReactNode,
    fallback: React.ReactNode = null
  ): React.ReactNode => {
    const hasAccess = checker.check(requirement).hasAccess;
    return hasAccess ? component : fallback;
  }, [checker]);

  const renderIfAny = useCallback((
    features: FeaturePermission[],
    component: React.ReactNode,
    fallback: React.ReactNode = null
  ): React.ReactNode => {
    return renderIf({ features, requireAll: false }, component, fallback);
  }, [renderIf]);

  const renderIfAll = useCallback((
    features: FeaturePermission[],
    component: React.ReactNode,
    fallback: React.ReactNode = null
  ): React.ReactNode => {
    return renderIf({ features, requireAll: true }, component, fallback);
  }, [renderIf]);

  return { renderIf, renderIfAny, renderIfAll };
};

/**
 * Hook for permission-based feature flags
 */
export const useFeatureFlags = () => {
  const { context } = usePermissions();

  return useMemo(() => ({
    // Feature flags based on permissions
    canUseAdvancedAnalytics: context.userPermissions.includes('analytics_export'),
    canUseBulkOperations: context.isAdmin || context.isSuperAdmin,
    canAccessSystemSettings: context.isSuperAdmin,
    canManageCompanySettings: context.userPermissions.includes('company_write'),
    canViewFinancialData: context.userPermissions.some(p =>
      p.includes('payments_') || p.includes('invoices_')
    ),
    canManageMaintenance: context.userPermissions.some(p =>
      p.includes('maintenance_')
    )
  }), [context]);
};

/**
 * Hook for permission-based UI state
 */
export const usePermissionState = () => {
  const { context, checker } = usePermissions();

  return useMemo(() => {
    const hasFeaturePermissions = context.userPermissions.length > 0;

    return {
      hasFeaturePermissions,
      totalPermissions: context.userPermissions.length,
      hasElevatedAccess: checker.hasElevatedAccess(),
      accessLevels: {
        properties: checker.getAccessLevel('properties'),
        tenants: checker.getAccessLevel('tenants'),
        payments: checker.getAccessLevel('payments'),
        maintenance: checker.getAccessLevel('maintenance'),
        analytics: checker.getAccessLevel('analytics'),
        company: checker.getAccessLevel('company'),
        users: checker.getAccessLevel('users')
      }
    };
  }, [context, checker]);
};

/**
 * Hook for handling permission errors and notifications
 */
export const usePermissionFeedback = () => {
  const showPermissionDenied = useCallback((
    action: string,
    resource: string,
    missingPermissions?: FeaturePermission[]
  ) => {
    // This could integrate with your notification system
    console.warn(`Permission denied: Cannot ${action} ${resource}`, {
      missingPermissions
    });

    // You could dispatch a notification action here
    // dispatch(showNotification({
    //   type: 'error',
    //   message: `You don't have permission to ${action} ${resource}`
    // }));
  }, []);

  const showPermissionRequired = useCallback((features: FeaturePermission[]) => {
    console.warn('Permission required:', features);

    // Show user-friendly permission requirement message
    // dispatch(showNotification({
    //   type: 'warning',
    //   message: 'Additional permissions required for this action'
    // }));
  }, []);

  return { showPermissionDenied, showPermissionRequired };
};

/**
 * Hook for route permission checking
 */
export const useRoutePermissions = () => {
  const { checker, context, loading } = usePermissions();

  const canAccessRoute = useCallback((path: string): boolean => {
    if (loading || !checker || !context) return false;

    const { getRoutePermission } = require('../config/routePermissions');
    const routePermission = getRoutePermission(path);

    if (!routePermission) return true; // No restrictions = accessible

    // Check feature permissions
    if (routePermission.features && routePermission.features.length > 0) {
      const result = checker.check({
        features: routePermission.features,
        requireAll: routePermission.requireAll,
        minimumLevel: routePermission.minimumLevel
      });

      if (result.hasAccess) return true;
    }

    // Custom permission check
    if (routePermission.customCheck) {
      if (routePermission.customCheck(context)) return true;
    }

    // Role-based fallback
    if (routePermission.fallbackRoles && context.userRole) {
      return routePermission.fallbackRoles.includes(context.userRole);
    }

    return false;
  }, [checker, context, loading]);

  const getAccessibleRoutes = useCallback((): string[] => {
    if (loading || !checker || !context) return [];

    const { getAllProtectedRoutes } = require('../config/routePermissions');
    const allRoutes = getAllProtectedRoutes();

    return allRoutes.filter((route: string) => canAccessRoute(route));
  }, [canAccessRoute, loading, checker, context]);

  const getInaccessibleRoutes = useCallback((): string[] => {
    if (loading || !checker || !context) return [];

    const { getAllProtectedRoutes } = require('../config/routePermissions');
    const allRoutes = getAllProtectedRoutes();

    return allRoutes.filter((route: string) => !canAccessRoute(route));
  }, [canAccessRoute, loading, checker, context]);

  return {
    canAccessRoute,
    getAccessibleRoutes,
    getInaccessibleRoutes,
    loading
  };
};